import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * Connect a website chat for a voice agent (plan-004 WC-81, FR-WC-17, FR-WC-19, design §17.10.5).
 *
 * It asks two things: which live agent answers, and the address of the site the chat goes on. The
 * address becomes the widget's first allowed site (D2 (a): the chat answers only on listed sites), so
 * it is checked as it is typed with the same rule the editor uses — `normaliseOrigin` — and the API
 * normalises and checks it again. A voice agent that already has a widget gets that widget back,
 * switched on, with the address added, so a snippet already on the site works again.
 *
 * `?agent=<id>` (the Channels tab's "Get snippet") pre-selects that agent. An agent that is not live
 * cannot answer a chat, and the panel says so rather than silently choosing another.
 *
 * Refused by the API while web chat is still waitlisted (409); the panel says so from the detail's
 * own `waitlist_reason` and offers no button that would be refused.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Input, Select } from "@wetalk/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY } from "../../api/query.js";
import type { MessagingMediumDetailView } from "../../api/gateway.js";
import type { WebChatConnectedView } from "../../api/slice/messaging.js";
import { originAdviceOf } from "./origin-rule.js";
import { PartyAccessRequiredNote, isPartyAccessRequired } from "../agents/[id]/channels/access-binding-refusal.js";

export interface ConnectWidgetPanelProps {
  readonly medium: MessagingMediumDetailView;
  readonly canEdit: boolean;
  readonly refusedBecause: string | null;
  /** The chosen voice agent — held by the page so "Connect again" on a widget can choose it. */
  readonly chosen: string;
  readonly onChoose: (voiceAgentId: string) => void;
  /** `?agent=` when it names an agent that is not live: the panel says why it is not offered. */
  readonly unanswerableRequested: boolean;
  /** Bumped by "Connect again": the site address box takes the focus. */
  readonly focusRequest: number;
}

export function ConnectWidgetPanel({
  medium,
  canEdit,
  refusedBecause,
  chosen,
  onChoose,
  unanswerableRequested,
  focusRequest,
}: ConnectWidgetPanelProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [siteOrigin, setSiteOrigin] = useState("");
  const [tried, setTried] = useState(false);
  const [connected, setConnected] = useState<WebChatConnectedView | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (focusRequest === 0) return;
    const site = formRef.current?.querySelector<HTMLInputElement>('input[inputmode="url"]');
    site?.focus();
  }, [focusRequest]);

  const connect = useMutation({
    mutationFn: (request: { readonly voiceAgentId: string; readonly siteOrigin: string }) =>
      gateway.connectWebChatWidget(request.voiceAgentId, request.siteOrigin),
    onSuccess: async (outcome) => {
      setConnected(outcome);
      setSiteOrigin("");
      setTried(false);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.messagingMedium });
    },
  });

  const waitlisted = medium.availability === "waitlisted";
  const advice = originAdviceOf(siteOrigin);
  const siteProblem =
    advice.kind === "refused"
      ? advice.problem
      : advice.kind === "empty" && tried
        ? portalText("operations", "enter_the_address_of_the_website_the_chat_goes_on_such_as_90f083b7")
        : null;

  return (
    <section className="mg-card mg-connect" aria-labelledby="mg-connect-title">
      <h2 className="mg-card-title mg-card-title-spaced" id="mg-connect-title">
        {portalText("operations", "connect_a_website_chat_d7245983")}</h2>
      {waitlisted ? (
        <p className="c-empty-dek">
          {medium.waitlistReason ?? portalText("operations", "web_chat_is_not_open_yet_f6e6ea87")} {portalText("operations", "connecting_opens_the_day_it_does_37be30df")}</p>
      ) : !canEdit ? (
        <p className="c-empty-dek">{refusedBecause}</p>
      ) : medium.answerableVoiceAgent.length === 0 ? (
        <p className="c-empty-dek">
          {portalText("operations", "no_agent_is_live_in_this_workspace_yet_so_there_is_nothing_a1843cf5")}</p>
      ) : (
        <form
          ref={formRef}
          className="mg-connect-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setTried(true);
            setConnected(null);
            if (advice.kind !== "accepted" || chosen === "") return;
            connect.mutate({ voiceAgentId: chosen, siteOrigin: advice.origin });
          }}
        >
          {unanswerableRequested ? (
            <p className="c-note" role="note">
              {portalText("operations", "the_agent_you_came_from_is_not_live_so_it_cannot_answer_a_52f81a66")}</p>
          ) : null}
          <Field label={portalText("operations", "agent_that_answers_737d3bb5")}>
            {(control) => (
              <Select
                {...control}
                value={chosen}
                onChange={(event) => {
                  onChoose(event.target.value);
                }}
              >
                {medium.answerableVoiceAgent.map((one) => (
                  <option key={one.voiceAgentId} value={one.voiceAgentId}>
                    {one.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label={portalText("operations", "your_website_s_address_b5cb6b10")}
            {...(siteProblem !== null
              ? { error: siteProblem }
              : advice.kind === "accepted"
                ? { description: portalText("operations", "the_chat_will_answer_on_value_add_more_sites_after_connect_905c6289", { v0: String(advice.origin) }) }
                : { description: portalText("operations", "the_site_the_snippet_goes_on_such_as_https_www_example_com_4a814938") })}
          >
            {(control) => (
              <Input
                {...control}
                font="mono"
                inputMode="url"
                autoComplete="url"
                spellCheck={false}
                placeholder="https://www.example.com"
                value={siteOrigin}
                onChange={(event) => {
                  setSiteOrigin(event.target.value);
                  connect.reset();
                }}
              />
            )}
          </Field>
          <Button variant="primary" type="submit" block disabled={connect.isPending}>
            {connect.isPending ? portalText("operations", "connecting_fd3e7969") : portalText("operations", "connect_website_chat_24e25873")}
          </Button>
          {connect.isError ? (
            isPartyAccessRequired(connect.error) ? (
              // plan-029 WAPP-ACCESS: an agent for the customer's own team needs its list first (access_list_required).
              <PartyAccessRequiredNote error={connect.error} voiceAgentId={connect.variables?.voiceAgentId ?? chosen} />
            ) : (
              <p className="c-note c-note-critical" role="alert">
                {apiErrorText(connect.error, portalText("operations", "the_website_chat_could_not_be_connected_c6f48a40"))} {portalText("operations", "nothing_was_changed_c60d5d2a")}</p>
            )
          ) : null}
          {connected === null ? null : (
            <p className="c-note" role="status">
              {connected.created
                ? portalText("operations", "connected_value_answers_chats_on_value_copy_its_snippet_ab_7112dd3e", { v0: String(connected.widget.voiceAgentName), v1: String(connected.widget.allowedOrigin.join(", ")) })
                : portalText("operations", "value_s_website_chat_is_on_again_with_the_same_widget_key_1aade756", { v0: String(connected.widget.voiceAgentName), v1: String(connected.widget.allowedOrigin.join(", ")) })}
            </p>
          )}
          <p className="c-empty-dek">
            {portalText("operations", "nothing_appears_on_your_site_until_you_paste_the_snippet_a_5871da35")}</p>
        </form>
      )}
    </section>
  );
}
